import {
  teamsAddAdmin,
  teamsAddMember,
  teamsAddReviewer,
  teamsCreate,
  teamsDelete,
  teamsGetByName,
  teamsList,
  teamsListAdmins,
  teamsListMembers,
  teamsListReviewers,
  teamsRemoveAdmin,
  teamsRemoveMember,
  teamsRemoveReviewer,
  teamsUpdate,
} from '@/api/generated';
import {
  type ListQueryParams,
  fetchAllPages,
  fetchAllPagesWithTotal,
  flattenListParams,
} from '@/api/hooks/_helpers';
/**
 * Teams resource hooks. Backs the admin Teams tab + the public Teams list
 * / Team Detail pages (T15).
 *
 * Wrappers over the Orval-generated `teamsList` / `teamsGet` /
 * `teamsGetByName` / `teamsCreate` / `teamsUpdate` / `teamsDelete` fetchers.
 * The mutation wrappers own React Query cache invalidation for the
 * `['teams']` prefix.
 *
 * **Never resolve a slug by listing.** `/teams/:slug` is served by
 * {@link useTeamByName} against the slug-addressed route; the page used to
 * hydrate `useListTeams({ pageSize: 100 })` and `.find()` the slug, which
 * drops every team past the page. See ADR 0009.
 */
import { AUTH_ME_QUERY_KEY } from '@/api/hooks/auth';
import type { TeamsListAdminsParams } from '@/api/schemas/teamsListAdminsParams';
import type { TeamsListMembersParams } from '@/api/schemas/teamsListMembersParams';
import type { TeamsListParams } from '@/api/schemas/teamsListParams';
import type { TeamsListReviewersParams } from '@/api/schemas/teamsListReviewersParams';
import type { PaginatedResponse } from '@cotality/marketplace-contracts/endpoints';
import type {
  AddTeamAdminBody,
  AddTeamAdminResponse,
  AddTeamMemberBody,
  AddTeamMemberResponse,
  AddTeamReviewerBody,
  AddTeamReviewerResponse,
  CreateTeamBody,
  CreateTeamResponse,
  GetTeamByNameResponse,
  ListTeamAdminsResponse,
  ListTeamMembersResponse,
  ListTeamReviewersResponse,
  UpdateTeamBody,
  UpdateTeamResponse,
} from '@cotality/marketplace-contracts/endpoints/teams';
import type { Team } from '@cotality/marketplace-contracts/schemas/team';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

export interface UseListTeamsOptions {
  enabled?: boolean;
}

export function useListTeams(
  params?: ListQueryParams,
  options?: UseListTeamsOptions,
): UseQueryResult<PaginatedResponse<Team>> {
  const flat = flattenListParams(params) as TeamsListParams | undefined;
  return useQuery<PaginatedResponse<Team>>({
    queryKey: ['teams', params ?? {}],
    queryFn: ({ signal }) => teamsList(flat, signal) as Promise<PaginatedResponse<Team>>,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

/**
 * Fetch the COMPLETE team list (pages through all results) for building a
 * teamId→Team lookup or populating a team **picker**. Use when a page-capped
 * `useListTeams({ pageSize: 100 })` could silently drop teams from a
 * client-side resolution map or a dropdown (the capped-page-join bug class) —
 * a team absent from a picker cannot be selected at all, with no error to
 * notice. Teams are a provably-bounded set, so paging all of them is cheap.
 * Keyed under the `['teams']` prefix so team mutations'
 * `invalidateQueries({ queryKey: ['teams'] })` refresh it.
 */
export function useAllTeams(options?: UseListTeamsOptions): UseQueryResult<Team[]> {
  return useQuery<Team[]>({
    queryKey: ['teams', 'all'],
    queryFn: ({ signal }) =>
      fetchAllPages<Team>(
        (params) => teamsList(params, signal) as Promise<PaginatedResponse<Team>>,
      ),
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

/**
 * `GET /api/teams/by-name/:name` — slug-addressed fetch, the canonical
 * resolver for `/teams/:slug`. Returns the same row shape as
 * {@link useGetTeam}.
 *
 * Both the Team Detail page and its breadcrumb consume this hook, so they
 * share one React Query entry and the crumb costs zero extra network. A
 * missing slug surfaces as the query's 404 (`ApiError.status === 404`);
 * callers must not fall back to scanning a list page.
 *
 * Keyed under the `['teams']` prefix so team mutations' broad
 * `invalidateQueries({ queryKey: ['teams'] })` refresh it.
 */
export function useTeamByName(name: string | undefined): UseQueryResult<GetTeamByNameResponse> {
  return useQuery<GetTeamByNameResponse>({
    queryKey: ['teams', 'by-name', name ?? ''],
    queryFn: ({ signal }) => teamsGetByName(name ?? '', signal) as Promise<GetTeamByNameResponse>,
    enabled: typeof name === 'string' && name.length > 0,
  });
}

/**
 * Returns a stable callback that warms the by-name cache for `name` (the SAME
 * key {@link useTeamByName} reads). Call it on card hover/focus so the detail
 * page renders from cache on click. No-op if already cached/in-flight.
 *
 * Keyed by name, not id: `/teams/:slug` is slug-addressed, so an id-keyed
 * prefetch warms an entry nothing reads and the hover optimisation dies
 * silently. (It was keyed by id, against a page that resolved by slug.)
 */
export function usePrefetchTeamByName(): (name: string) => void {
  const qc = useQueryClient();
  return useCallback(
    (name: string) => {
      if (name.length === 0) return;
      void qc.prefetchQuery({
        queryKey: ['teams', 'by-name', name],
        queryFn: ({ signal }) => teamsGetByName(name, signal) as Promise<GetTeamByNameResponse>,
      });
    },
    [qc],
  );
}

export function useCreateTeam(): UseMutationResult<CreateTeamResponse, Error, CreateTeamBody> {
  const qc = useQueryClient();
  return useMutation<CreateTeamResponse, Error, CreateTeamBody>({
    mutationFn: (body) => teamsCreate(body) as Promise<CreateTeamResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useUpdateTeam(): UseMutationResult<
  UpdateTeamResponse,
  Error,
  { id: string; body: UpdateTeamBody }
> {
  const qc = useQueryClient();
  return useMutation<UpdateTeamResponse, Error, { id: string; body: UpdateTeamBody }>({
    mutationFn: ({ id, body }) => teamsUpdate(id, body) as Promise<UpdateTeamResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useDeleteTeam(): UseMutationResult<void, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => teamsDelete(id) as Promise<void>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * `GET /api/teams/:id/admins` — paginated list of active `team_admin`
 * grants for a team. Disabled when `teamId === undefined` (mirrors
 * `useGetTeam`) so the sub-resource never fires against
 * `/api/teams/undefined/admins`.
 *
 * The grant rows embed `displayName` + `email` (server-side `LEFT JOIN
 * users`), so the Team page renders names directly — no client-side
 * `/api/users` join (which silently dropped admins once the user count
 * crossed a single capped page).
 */
export function useListTeamAdmins(
  teamId: string | undefined,
  params?: ListQueryParams,
): UseQueryResult<ListTeamAdminsResponse> {
  const flat = flattenListParams(params) as TeamsListAdminsParams | undefined;
  return useQuery<ListTeamAdminsResponse>({
    queryKey: ['teams', teamId ?? '', 'admins', params ?? {}],
    queryFn: ({ signal }) =>
      teamsListAdmins(teamId ?? '', flat, signal) as Promise<ListTeamAdminsResponse>,
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/**
 * COMPLETE admin roster for a team — pages through every result and carries
 * the server `total`. A team roster is a provably-bounded label set (R3), and
 * the merged People table must show every member: a single capped page would
 * silently drop rows past `pageSize` from a table that reads as complete.
 *
 * Returns the same `{items, total, …}` envelope as
 * {@link useListTeamAdmins} so callers read `.total` for counts and `.items`
 * for the roster. Keyed under `['teams', teamId, 'admins']` so the existing
 * grant/revoke invalidations refresh it.
 */
export function useAllTeamAdmins(
  teamId: string | undefined,
): UseQueryResult<ListTeamAdminsResponse> {
  return useQuery<ListTeamAdminsResponse>({
    queryKey: ['teams', teamId ?? '', 'admins', 'all'],
    queryFn: ({ signal }) =>
      fetchAllPagesWithTotal(
        (params) =>
          teamsListAdmins(teamId ?? '', params, signal) as Promise<ListTeamAdminsResponse>,
      ),
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/**
 * COMPLETE reviewer roster for a team. Same rationale as
 * {@link useAllTeamAdmins}, plus one that is load-bearing for correctness:
 * `total` gates the "no reviewers — PRs can't merge" banner, and a count
 * derived from a truncated page can suppress that warning outright.
 */
export function useAllTeamReviewers(
  teamId: string | undefined,
): UseQueryResult<ListTeamReviewersResponse> {
  return useQuery<ListTeamReviewersResponse>({
    queryKey: ['teams', teamId ?? '', 'reviewers', 'all'],
    queryFn: ({ signal }) =>
      fetchAllPagesWithTotal(
        (params) =>
          teamsListReviewers(teamId ?? '', params, signal) as Promise<ListTeamReviewersResponse>,
      ),
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/** COMPLETE member roster for a team. Same rationale as {@link useAllTeamAdmins}. */
export function useAllTeamMembers(
  teamId: string | undefined,
): UseQueryResult<ListTeamMembersResponse> {
  return useQuery<ListTeamMembersResponse>({
    queryKey: ['teams', teamId ?? '', 'members', 'all'],
    queryFn: ({ signal }) =>
      fetchAllPagesWithTotal(
        (params) =>
          teamsListMembers(teamId ?? '', params, signal) as Promise<ListTeamMembersResponse>,
      ),
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/**
 * `POST /api/teams/:id/admins` — grant `team_admin` to a user. Idempotent
 * on existing non-deleted grants (returns 201 on a fresh insert, 200 on a
 * re-grant).
 *
 * Invalidates:
 *   - `['teams', teamId, 'admins']` — the admin roster for this team
 *   - `['teams', 'detail', teamId]` — the team detail entry, so any
 *     follow-up that coalesces admin grants with detail mutations cannot
 *     leave the cached detail row stale
 *   - `['users']` — granting `team_admin` flips the user's roles array
 *   - `AUTH_ME_QUERY_KEY` — if the caller granted to themselves, their
 *     effective permissions change. Cheaper to invalidate unconditionally
 *     than to introspect the principal.
 *
 * Mutator preserves `ApiError.code` (`LAST_ADMIN`, etc.) so callers can
 * branch on the error code for inline-toast messaging.
 */
export function useAddTeamAdmin(): UseMutationResult<
  AddTeamAdminResponse,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<AddTeamAdminResponse, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) =>
      teamsAddAdmin(teamId, { userId } satisfies AddTeamAdminBody) as Promise<AddTeamAdminResponse>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'admins'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });
}

/**
 * `DELETE /api/teams/:id/admins/:userId` — soft-revoke a grant. Returns
 * 204 on success; the service-layer last-admin guard surfaces as a 409
 * `LAST_ADMIN` `ApiError` so the UI can render the inline-toast message.
 *
 * Invalidations match `useAddTeamAdmin`.
 */
export function useRemoveTeamAdmin(): UseMutationResult<
  void,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) => teamsRemoveAdmin(teamId, userId) as Promise<void>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'admins'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });
}

/**
 * `GET /api/teams/:id/reviewers` — paginated list of active reviewer grants
 * for a team. Disabled when `teamId === undefined` so the sub-resource never
 * fires against `/api/teams/undefined/reviewers`.
 */
export function useListTeamReviewers(
  teamId: string | undefined,
  params?: ListQueryParams,
): UseQueryResult<ListTeamReviewersResponse> {
  const flat = flattenListParams(params) as TeamsListReviewersParams | undefined;
  return useQuery<ListTeamReviewersResponse>({
    queryKey: ['teams', teamId ?? '', 'reviewers', params ?? {}],
    queryFn: ({ signal }) =>
      teamsListReviewers(teamId ?? '', flat, signal) as Promise<ListTeamReviewersResponse>,
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/**
 * `POST /api/teams/:id/reviewers` — add a reviewer grant. Idempotent on
 * re-add (200 on existing, 201 on fresh).
 *
 * Invalidates:
 *   - `['teams', teamId, 'reviewers']` — the reviewer roster for this team
 *   - `['teams', 'detail', teamId]` — the team detail entry
 *   - `['users']` — a reviewer change flips the user's team-roles view
 *     (mirrors `useAddTeamAdmin`), so a change made anywhere refreshes the
 *     user-page Teams tab, not only when routed through the container.
 */
export function useAddTeamReviewer(): UseMutationResult<
  AddTeamReviewerResponse,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<AddTeamReviewerResponse, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) =>
      teamsAddReviewer(teamId, {
        userId,
      } satisfies AddTeamReviewerBody) as Promise<AddTeamReviewerResponse>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'reviewers'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * `DELETE /api/teams/:id/reviewers/:userId` — soft-revoke a reviewer grant.
 * Returns 204 on success. No last-reviewer guard (an empty Reviewers team is
 * a valid ADO PR-gating state).
 *
 * Invalidations match `useAddTeamReviewer` (incl. `['users']` so the
 * user-page Teams tab refreshes on a reviewer revoke made anywhere).
 */
export function useRemoveTeamReviewer(): UseMutationResult<
  void,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) => teamsRemoveReviewer(teamId, userId) as Promise<void>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'reviewers'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * `GET /api/teams/:id/members` — paginated list of active member grants for a
 * team. The "member" tier is a plain user↔team association: NO permissions, NO
 * ADO mirroring, purely cosmetic rostering — so the grant shape carries no
 * `adoSyncPending` companion. Disabled when `teamId === undefined` so the
 * sub-resource never fires against `/api/teams/undefined/members`.
 */
export function useListTeamMembers(
  teamId: string | undefined,
  params?: ListQueryParams,
): UseQueryResult<ListTeamMembersResponse> {
  const flat = flattenListParams(params) as TeamsListMembersParams | undefined;
  return useQuery<ListTeamMembersResponse>({
    queryKey: ['teams', teamId ?? '', 'members', params ?? {}],
    queryFn: ({ signal }) =>
      teamsListMembers(teamId ?? '', flat, signal) as Promise<ListTeamMembersResponse>,
    enabled: typeof teamId === 'string' && teamId.length > 0,
  });
}

/**
 * `POST /api/teams/:id/members` — add a member grant. Idempotent on re-add
 * (200 on existing, 201 on fresh). Members never sync to ADO, so there is no
 * pending state to surface.
 *
 * Invalidations mirror `useAddTeamReviewer`:
 *   - `['teams', teamId, 'members']` — the member roster for this team
 *   - `['teams', 'detail', teamId]` — the team detail entry
 *   - `['users']` — a member change flips the user's team-roles view, so a
 *     change made anywhere refreshes the user-page Teams tab.
 */
export function useAddTeamMember(): UseMutationResult<
  AddTeamMemberResponse,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<AddTeamMemberResponse, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) =>
      teamsAddMember(teamId, {
        userId,
      } satisfies AddTeamMemberBody) as Promise<AddTeamMemberResponse>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'members'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * `DELETE /api/teams/:id/members/:userId` — soft-revoke a member grant.
 * Returns 204 on success. No last-member guard (an empty member roster is a
 * valid state).
 *
 * Invalidations match `useAddTeamMember` (incl. `['users']` so the user-page
 * Teams tab refreshes on a member revoke made anywhere).
 */
export function useRemoveTeamMember(): UseMutationResult<
  void,
  Error,
  { teamId: string; userId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { teamId: string; userId: string }>({
    mutationFn: ({ teamId, userId }) => teamsRemoveMember(teamId, userId) as Promise<void>,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['teams', variables.teamId, 'members'] });
      void qc.invalidateQueries({ queryKey: ['teams', 'detail', variables.teamId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
